import React, {useState} from 'react';
import axios from 'axios';

import {
    Box,
    Button,
    Container,
    Divider,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    Checkbox,
    FormControlLabel
} from '@mui/material';

import {styled} from '@mui/system';

import ColorPicker from './ColorPicker';

const FormWrapper = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    marginTop: '25px',
    marginBottom: '25px',
});

const FormContainer = styled(Container)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
    padding: '2rem',
    borderRadius: '12px',
    width: '100%',
});

function QrForm({setQrCode, setLoading, setError, backend}) {

    const [formData, setFormData] = useState({
        content_type: 'url',
        data: '',
        logo: null,

        foreground_color: '#000000',
        background_color: '#FFFFFF',

        gradient_start: '#000000',
        gradient_end: '#ff0000',
        gradient_direction: 'diagonal',
        use_gradient: false,

        module_shape: 'square',

        eye_shape: 'square',
        eye_radius: 0.35,

        size: 10,
        border: 4,

        // vCard fields
        first_name: '',
        last_name: '',
        full_name: '',
        email: '',
        phone: '',
        company: '',
        title: '',
        website: '',
        street: '',
        city: '',
        region: '',
        zip: '',
        country: '',

        // WIFI fields
        wifi_ssid: '',
        wifi_password: '',
        wifi_encryption: 'WPA',
        wifi_hidden: false,

        // COLOR field
        color_value: '#000000'
    });

    const [isLoading, setIsLoading] = useState(false);

    function handleChange(e) {

        const {
            name,
            value,
            type,
            checked
        } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox'
                ? checked
                : value
        }));
    }

    function handleFileChange(e) {

        const file = e.target.files[0];

        if (file) {

            const reader = new FileReader();

            reader.onloadend = function () {

                const base64String =
                    reader.result.split(',')[1];

                setFormData(prev => ({
                    ...prev,
                    logo: base64String
                }));
            };

            reader.readAsDataURL(file);
        }
    }

    async function handleSubmit(e) {

        e.preventDefault();

        setIsLoading(true);
        setLoading(true);
        setError(null);

        try {

            let dataToSend = {
                ...formData
            };

            if (formData.content_type === 'vcard') {

                const vcardData = {
                    first_name: formData.first_name,
                    last_name: formData.last_name,
                    full_name: formData.full_name,
                    email: formData.email,
                    phone: formData.phone,
                    company: formData.company,
                    title: formData.title,
                    website: formData.website,

                    address: {
                        street: formData.street,
                        city: formData.city,
                        region: formData.region,
                        zip: formData.zip,
                        country: formData.country
                    }
                };

                dataToSend = {
                    ...dataToSend,
                    data: vcardData
                };
            }

            if (formData.content_type === 'wifi') {

                const wifiString =
                    `WIFI:T:${formData.wifi_encryption};S:${formData.wifi_ssid};P:${formData.wifi_password};H:${formData.wifi_hidden};;`;

                dataToSend = {
                    ...dataToSend,
                    data: wifiString
                };
            }

            if (formData.content_type === 'color') {

                dataToSend = {
                    ...dataToSend,
                    data: formData.color_value
                };
            }

            const response = await axios.post(
                backend + '/generate',
                dataToSend,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            setQrCode(response.data.data);

        } catch (err) {

            setError(
                err.response?.data?.message ||
                'Error generating QR code'
            );

        } finally {

            setIsLoading(false);
            setLoading(false);
        }
    }

    const renderContentField = () => {

        if (formData.content_type === 'color') {

            return (
                <Container maxWidth="sm" style={{marginTop: '10px'}}>
                    <Grid container spacing={3}>

                        <Grid item xs={12}>
                            <Typography variant="body2">
                                Wählen Sie eine Farbe.
                                Der QR-Code enthält den HEX-Wert.
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <ColorPicker
                                label="Farbe"
                                name="color_value"
                                value={formData.color_value}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="HEX Wert"
                                name="color_value"
                                value={formData.color_value}
                                onChange={handleChange}
                            />
                        </Grid>

                    </Grid>
                </Container>
            );
        }

        if (formData.content_type === 'wifi') {

            return (
                <Container maxWidth="sm" style={{marginTop: '10px'}}>
                    <Grid container spacing={3}>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="WLAN Name (SSID)"
                                name="wifi_ssid"
                                value={formData.wifi_ssid}
                                onChange={handleChange}
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Passwort"
                                name="wifi_password"
                                value={formData.wifi_password}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth>

                                <InputLabel>
                                    Verschlüsselung
                                </InputLabel>

                                <Select
                                    name="wifi_encryption"
                                    value={formData.wifi_encryption}
                                    onChange={handleChange}
                                    label="Verschlüsselung"
                                >
                                    <MenuItem value="WPA">
                                        WPA/WPA2
                                    </MenuItem>

                                    <MenuItem value="WEP">
                                        WEP
                                    </MenuItem>

                                    <MenuItem value="nopass">
                                        Keine
                                    </MenuItem>

                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="wifi_hidden"
                                        checked={formData.wifi_hidden}
                                        onChange={handleChange}
                                    />
                                }
                                label="Verstecktes Netzwerk"
                            />
                        </Grid>

                    </Grid>
                </Container>
            );
        }

        if (formData.content_type === 'vcard') {

            return (
                <Container maxWidth="sm" style={{marginTop: '10px'}}>
                    <Grid container spacing={3}>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Vorname"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleChange}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Nachname"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleChange}
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Vollständiger Name"
                                name="full_name"
                                value={formData.full_name}
                                onChange={handleChange}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Mobilnummer"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Firma"
                                name="company"
                                value={formData.company}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Titel"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Webseite"
                                name="website"
                                value={formData.website}
                                onChange={handleChange}
                                placeholder="https://example.com"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{my: 1}} />

                            <Typography variant="subtitle1">
                                Adresse
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Straße"
                                name="street"
                                value={formData.street}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="PLZ"
                                name="zip"
                                value={formData.zip}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                label="Ort"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Bundesland / Region"
                                name="region"
                                value={formData.region}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Land"
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                            />
                        </Grid>

                    </Grid>
                </Container>
            );
        }

        return (
            <Grid item xs={12}>

                <TextField
                    fullWidth
                    label={
                        formData.content_type === 'url'
                            ? 'URL'
                            : 'Email'
                    }
                    name="data"
                    value={formData.data}
                    onChange={handleChange}
                    placeholder={
                        formData.content_type === 'url'
                            ? 'https://example.com'
                            : 'email@example.com'
                    }
                    required
                />

            </Grid>
        );
    };

    return (
        <FormWrapper>

            <FormContainer maxWidth="md">

                <form onSubmit={handleSubmit}>

                    <Grid container spacing={3}>

                        <Grid item xs={12}>

                            <Typography
                                variant="h5"
                                gutterBottom
                            >
                                QR-Code erstellen
                            </Typography>

                        </Grid>

                        <Grid item xs={12}>

                            <FormControl fullWidth>

                                <InputLabel>
                                    QR Code Typ
                                </InputLabel>

                                <Select
                                    name="content_type"
                                    value={formData.content_type}
                                    onChange={handleChange}
                                    label="QR Code Typ"
                                >
                                    <MenuItem value="url">
                                        URL
                                    </MenuItem>

                                    <MenuItem value="email">
                                        Email
                                    </MenuItem>

                                    <MenuItem value="vcard">
                                        vCard
                                    </MenuItem>

                                    <MenuItem value="wifi">
                                        WLAN
                                    </MenuItem>

                                    <MenuItem value="color">
                                        Farbe
                                    </MenuItem>

                                </Select>

                            </FormControl>

                        </Grid>

                        {renderContentField()}

                        <Grid item xs={12}>

                            <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                            >
                                Logo hochladen

                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={handleFileChange}
                                />

                            </Button>

                            {formData.logo && (
                                <FormHelperText>
                                    Logo ausgewählt
                                </FormHelperText>
                            )}

                        </Grid>

                        <Grid item xs={12}>
                            <Divider />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <ColorPicker
                                label="QR-Code Farbe"
                                name="foreground_color"
                                value={formData.foreground_color}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <ColorPicker
                                label="Hintergrund Farbe"
                                name="background_color"
                                value={formData.background_color}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{mb: 1}} />

                            <Typography
                                variant="h6"
                                gutterBottom
                            >
                                QR-Code Stil
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>

                            <FormControl fullWidth>

                                <InputLabel>
                                    Form der Elemente
                                </InputLabel>

                                <Select
                                    name="module_shape"
                                    value={formData.module_shape}
                                    onChange={handleChange}
                                    label="Form der Elemente"
                                >
                                    <MenuItem value="square">
                                        Kästchen
                                    </MenuItem>

                                    <MenuItem value="circle">
                                        Kreise
                                    </MenuItem>

                                    <MenuItem value="heart">
                                        Herzen
                                    </MenuItem>

                                </Select>

                            </FormControl>

                        </Grid>

                        <Grid item xs={12} md={6}>

                            <FormControl fullWidth>

                                <InputLabel>
                                    Form der Ecken
                                </InputLabel>

                                <Select
                                    name="eye_shape"
                                    value={formData.eye_shape}
                                    onChange={handleChange}
                                    label="Form der Ecken"
                                >
                                    <MenuItem value="square">
                                        Eckig
                                    </MenuItem>

                                    <MenuItem value="circle">
                                        Rund
                                    </MenuItem>

                                </Select>

                            </FormControl>

                        </Grid>

                        {formData.eye_shape === 'circle' && (

                            <Grid item xs={12}>

                                <TextField
                                    fullWidth
                                    label="Radius der Ecken"
                                    name="eye_radius"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.05
                                    }}
                                    value={formData.eye_radius}
                                    onChange={handleChange}
                                    helperText="Größere Werte erzeugen stärkere Rundungen"
                                />

                            </Grid>

                        )}

                        <Grid item xs={12}>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.use_gradient}
                                        onChange={handleChange}
                                        name="use_gradient"
                                    />
                                }
                                label="Farbverlauf verwenden"
                            />

                        </Grid>

                        {formData.use_gradient && (
                            <>
                                <Grid item xs={12} md={4}>
                                    <ColorPicker
                                        label="Startfarbe"
                                        name="gradient_start"
                                        value={formData.gradient_start}
                                        onChange={handleChange}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <ColorPicker
                                        label="Endfarbe"
                                        name="gradient_end"
                                        value={formData.gradient_end}
                                        onChange={handleChange}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>

                                    <FormControl fullWidth>

                                        <InputLabel>
                                            Verlauf
                                        </InputLabel>

                                        <Select
                                            name="gradient_direction"
                                            value={formData.gradient_direction}
                                            onChange={handleChange}
                                            label="Verlauf"
                                        >
                                            <MenuItem value="horizontal">
                                                Horizontal
                                            </MenuItem>

                                            <MenuItem value="vertical">
                                                Vertikal
                                            </MenuItem>

                                            <MenuItem value="diagonal">
                                                Diagonal
                                            </MenuItem>

                                            <MenuItem value="radial">
                                                Radial
                                            </MenuItem>

                                        </Select>

                                    </FormControl>

                                </Grid>
                            </>
                        )}

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Größe"
                                name="size"
                                type="number"
                                inputProps={{
                                    min: 5,
                                    max: 20
                                }}
                                value={formData.size}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Rahmen"
                                name="border"
                                type="number"
                                inputProps={{
                                    min: 1,
                                    max: 10
                                }}
                                value={formData.border}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12}>

                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                fullWidth
                                size="large"
                                disabled={isLoading}
                            >
                                {isLoading
                                    ? 'Generating...'
                                    : 'Generate QR Code'}
                            </Button>

                        </Grid>

                    </Grid>

                </form>

            </FormContainer>

        </FormWrapper>
    );
}

export default QrForm;